import React, { useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import "../css/DeeplinkLoginDialog.css";
import letsDoItIcon from "../../assets/images/lets-do-it-icon.svg";
import PhoneInput from "react-phone-input-2";
import parsePhoneNumberFromString from "libphonenumber-js";
import DeeplinkLoginOTPView from "./DeeplinkLoginOTPView";
import { EMAIL_REGEX, USER_ROLE_LIST, USER_TYPE } from "utils/constants";
import { useMutation } from "@tanstack/react-query";
import useWSMApi from "services/api/useWSMApi";
import { trackLogin } from "utils/analytics";
import { useStorage } from "services/StorageProvider";
import BottomSheet from "./BottomSheet";
import SnackbarAlert from "./SnackbarAlert";

interface DeeplinkLoginDialogProps {
  open: boolean;
  isFocusHousePass: boolean;
  onClose: () => void;
  onCompleteLogin: () => void;
}

const RESEND_DELAY_SECONDS = 120;

const DeeplinkLoginDialog: React.FC<DeeplinkLoginDialogProps> = ({
  open,
  isFocusHousePass,
  onClose,
  onCompleteLogin,
}) => {
  const { registerUser, sendLoginOTP, validateLoginOTP, updateUserProfile } = useWSMApi();
  const { updateUserProfileData } = useStorage();
  const [stepCount, setStepCount] = useState(1);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [otp, setOtp] = useState<string>("");
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isValidPhoneNumber = (number: string) => {
    const normalized = number.replace(/^\+/, "");
    const phoneNumber = parsePhoneNumberFromString("+" + normalized);
    return phoneNumber ? phoneNumber.isValid() : false;
  };
  const [isFounder, setIsFounder] = useState(() => {
    const value = sessionStorage.getItem("isFounder");
    return value !== null ? JSON.parse(value) : null;
  });
  const [userRole, setUserRole] = useState<string>("");
  const [profileLink, setProfileLink] = useState(() => {
    return sessionStorage.getItem("profileLink") || "";
  });
  const [resendTimer, setResendTimer] = useState<number>(0);
  const [phoneBlurred, setPhoneBlurred] = useState(false);

  const validateEmail = (email: string): boolean => {
    return EMAIL_REGEX.test(email);
  };

  const isFirstStepDisabled =
    !firstName.trim() ||
    !mobileNumber.trim() ||
    !isValidPhoneNumber(mobileNumber) ||
    !userEmail.trim() ||
    !validateEmail(userEmail);
  const isSecondStepDisabled = isFocusHousePass
    ? !userRole
    : isFounder === null || !profileLink.trim();


  const registerUserMutation = useMutation({
    mutationFn: () => registerUser({ email: userEmail, type: USER_TYPE }),
    onSuccess: (data) => {
      localStorage.setItem("apiAccessToken", data?.accessToken);
      trackLogin();
      updateUserMutation.mutate();
    },
  });

  const sendOTPMutation = useMutation({
    mutationFn: () => sendLoginOTP({ email: userEmail, type: USER_TYPE }),
    onSuccess: () => {
      startResendTimer();
      setStepCount(3);
    },
  });

  const validateOTPMutation = useMutation({
    mutationFn: () =>
      validateLoginOTP({
        email: userEmail,
        otp: otp,
        type: USER_TYPE,
      }),
    onSuccess: (data) => {
      localStorage.setItem("apiAccessToken", data?.accessToken);
      trackLogin();
      updateUserMutation.mutate();
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: () =>
      updateUserProfile({
        first_name: firstName,
        last_name: lastName,
        mobile_number: "+" + mobileNumber,
        profile_link: profileLink,
        ...(isFounder && { is_founder: isFounder }),
        ...(isFocusHousePass && { role: userRole }),
      }),
    onSuccess: (response) => {
      updateUserProfileData(response);
      onCompleteLogin();
    },
  });

  const startResendTimer = () => {
    setResendTimer(RESEND_DELAY_SECONDS);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    timerRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleFirstStepContinueClick = (): void => {
    if (!isFirstStepDisabled) {
      setStepCount(2);
    }
  };

  const handleSecondStepContinueClick = (): void => {
    if (!isSecondStepDisabled) {
      // sendOTPMutation.mutate();
      registerUserMutation.mutate();
    }
  };

  const handleChangeEmailClick = (): void => {
    setStepCount(1);
    setResendTimer(0);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const handleVerifyOTPClick = (): void => {
    validateOTPMutation.mutate();
  };

  const handleOtpChange = (value: string): void => {
    setOtp(value);
  };

  const handleResendOTPClick = (): void => {
    if (resendTimer === 0) {
      sendOTPMutation.mutate();
      startResendTimer();
    }
  };

  return (
    <BottomSheet
      open={open}
      onClose={() => {
        setStepCount(1);
        setPhoneBlurred(false);
        onClose();
      }}
      height="70dvh"
    >
      <div>
        {sendOTPMutation?.isError ? (
          <SnackbarAlert type={"error"}>
            {sendOTPMutation.error.message}
          </SnackbarAlert>
        ) : (
          ""
        )}
        {validateOTPMutation?.isError ? (
          <SnackbarAlert type={"error"}>
            {validateOTPMutation.error.message}
          </SnackbarAlert>
        ) : (
          ""
        )}
        <div className="deeplink-login-header-container">
          <div className="deeplink-login-header-text-container">
            {stepCount === 3 ? <span>
              Let's Get you <span style={{ color: "white" }}>Verified !</span>
            </span> : <span>
              Let's Get to <span style={{ color: "white" }}>Know You !</span>
            </span>}
          </div>
          <div className="deeplink-login-header-container-divider"></div>
          <div className="deeplink-login-header-button-container">
            <div
              className="deeplink-login-header-container-button"
              onClick={() => {
                setStepCount(1);
                setPhoneBlurred(false);
                onClose();
              }}
            >
              ✕
            </div>
          </div>
        </div>
        <div className="deeplink-login-divider"></div>
        <div className="deeplink-login-dialog-content-container">
          {stepCount === 1 ? (
            <>
              <div className="deeplink-login-dialog-item-container">
                <div className="row-container">
                  <span>
                    First <span className="sub-text">Name</span>
                    <span className="required">*</span>
                  </span>
                  <span>
                    Last <span className="sub-text">Name</span>
                  </span>
                </div>
                <div className="row-container">
                  <input
                    autoFocus
                    placeholder="Enter First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                  <input
                    placeholder="Enter Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
              </div>
              <div className="deeplink-login-dialog-item-container">
                <span>
                  Phone <span className="sub-text">Number</span>
                  <span className="required">*</span>
                </span>
                <PhoneInput
                  country={"in"}
                  value={mobileNumber}
                  onChange={setMobileNumber}
                  onBlur={() => setPhoneBlurred(true)}
                  countryCodeEditable={false}
                  inputProps={{
                    name: "mobile",
                    required: true,
                  }}
                  containerClass={`deeplink-login-custom-phone${phoneBlurred && !isValidPhoneNumber(mobileNumber) ? " deeplink-login-phone-error" : ""}`}
                  inputClass="deeplink-login-custom-phone-input"
                />
              </div>
              <div className="deeplink-login-dialog-item-container">
                <span>
                  Email <span className="sub-text">Address</span>
                  <span className="required">*</span>
                </span>
                <input
                  type="email"
                  placeholder="youremail@address.com"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                />
              </div>
              <div
                className="deeplink-login-dialog-content-button"
                onClick={handleFirstStepContinueClick}
                style={{
                  opacity: !isFirstStepDisabled ? 1 : 0.5,
                }}
              >
                You’re Almost Done..
                <img
                  src={letsDoItIcon}
                  className="signin-otp-view-arrow-icon"
                  alt="logo"
                />
              </div>
            </>
          ) : stepCount === 2 ? (
            <>
              <div className="deeplink-login-dialog-item-container">
                <span>
                  Ur <span className="sub-text">Professional Profile</span>
                  {!isFocusHousePass && <span className="required">*</span>}
                </span>
                <input
                  placeholder="🔗 Enter Website/Linkedin/Instagram Link"
                  value={profileLink}
                  onChange={(e) => setProfileLink(e.target.value)}
                />
              </div>
              <div className="deeplink-login-dialog-item-container">
                <span>
                  {isFocusHousePass ? <span>Select your <span className="sub-text">role</span></span> : <span>Are you a <span className="sub-text">founder/owner ?</span></span>}
                  <span className="required">*</span>
                </span>
                {isFocusHousePass ? (
                  <select
                    className="deeplink-login-role-select"
                    value={userRole}
                    onChange={(e) => setUserRole(e.target.value)}
                  >
                    <option value="" disabled>Select your role</option>
                    {USER_ROLE_LIST.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="button-container">
                    <button
                      className={isFounder === true ? "selected" : ""}
                      onClick={() => setIsFounder(true)}
                    >
                      Yes
                    </button>
                    <button
                      className={isFounder === false ? "selected" : ""}
                      onClick={() => setIsFounder(false)}
                    >
                      No
                    </button>
                  </div>
                )}
              </div>
              <div
                className="deeplink-login-dialog-content-button"
                onClick={() =>
                  !sendOTPMutation?.isPending && handleSecondStepContinueClick()
                }
                style={{
                  opacity: !isSecondStepDisabled ? 1 : 0.5,
                }}
              >
                {sendOTPMutation?.isPending ? (
                  <CircularProgress size={30} color="inherit" />
                ) : (
                  <>
                    Continue
                    <img
                      src={letsDoItIcon}
                      className="signin-otp-view-arrow-icon"
                      alt="logo"
                    />
                  </>
                )}
              </div>
            </>
          ) : (
            <>
              <DeeplinkLoginOTPView
                userEmail={userEmail}
                onClickVerify={handleVerifyOTPClick}
                onChange={handleOtpChange}
                onClickChangeEmail={handleChangeEmailClick}
                isLoading={
                  validateOTPMutation?.isPending ||
                  updateUserMutation?.isPending
                }
                resendTimer={resendTimer}
                onClickResendOTP={handleResendOTPClick}
              />
            </>
          )}
        </div>
      </div>
    </BottomSheet>
  );
};

export default DeeplinkLoginDialog;
